import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {findGlobalSelectedFilter, findSelectedFilterForTeammate} from '../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../models/api/localPreferencesModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {VisualWorkspaceTeammate} from '../../../models/api/visualWorkspaceModel';
import {GlobalFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {toggleExpandItemAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  getFindIsExpanded,
  getSelectedFilters,
} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {getShouldAlphabetizeWorkspace} from '../../../store/data/preferences/preferencesSelectors';
import {getIsUserGlobalAdmin, getUserLicenseType} from '../../../store/data/teammates/teammatesSelectors';
import {makeGetVisualWorkspaceVisibleTeammates} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {TeammatesWorkspace} from './teammatesWorkspace';

/*
 * Constants.
 */

const teammatesWorkspaceExpandedKey = `${CollapsedItemKeyPrefixesEnum.WORKSPACE}teammates`;
const defaultIsTeammatesWorkspaceExpanded = true;

/*
 * Props.
 */

interface TeammatesWorkspaceContainerProps {
  visualWorkspaceTeammates: ReadonlyArray<VisualWorkspaceTeammate>;
}

/*
 * Selectors.
 */

function getVisualWorkspaceTeammates(_state: AppState, props: TeammatesWorkspaceContainerProps) {
  return props.visualWorkspaceTeammates;
}

const getIsExpanded = createSelector(getFindIsExpanded, (findIsExpanded) =>
  findIsExpanded(teammatesWorkspaceExpandedKey, defaultIsTeammatesWorkspaceExpanded),
);

const getGlobalAssignedFilter = createSelector(getSelectedFilters, (selectedFilters) =>
  findGlobalSelectedFilter(selectedFilters, GlobalFoldersEnum.ASSIGNED),
);

const getFindSelectedFilter = createSelector(
  getSelectedFilters,
  (selectedFilters) => (teammate: TeammateModel) =>
    findSelectedFilterForTeammate(selectedFilters, teammate.id),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeammatesWorkspaceContainerProps>()(() => ({
  isExpanded: getIsExpanded,
  globalAssignedFilter: getGlobalAssignedFilter,
  userLicenseType: getUserLicenseType,
  isUserGlobalAdmin: getIsUserGlobalAdmin,
  teammates: makeGetVisualWorkspaceVisibleTeammates(getVisualWorkspaceTeammates),
  findSelectedFilter: getFindSelectedFilter,
  shouldAlphabetizeWorkspace: getShouldAlphabetizeWorkspace,
}));

export const TeammatesWorkspaceContainer = connect(
  mapStateToProps,
  {
    toggleExpandItem: toggleExpandItemAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.TEAMMATES,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItem(teammatesWorkspaceExpandedKey, defaultIsTeammatesWorkspaceExpanded);
    };

    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
    };
  },
)(TeammatesWorkspace);
